<template>
  <v-app class="glassy-app">
    <v-content class="login-content" fill-height>
      <v-container fluid fill-height class="login-container">
        <v-row justify="center" align="center" class="fill-height">
          <v-col cols="12" sm="8" md="6" lg="4" xl="4">
            <!-- Glassy Card with fully translucent form -->
            <v-card flat class="glassy-card slide-in">
              <div class="card-inner">
                <div class="login-header">
                  <img src="@/assets/Back/images/ol-pejeta-logo.svg"
                       alt="Ol Pejeta Conservancy"
                       class="logo-image" />
                  <h1 class="welcome-title">Welcome Back</h1>
                  <p class="subtitle-text">Sign in to continue your journey</p>
                </div>

                <v-form lazy-validation @submit.prevent="doAuth()" flat class="glassy-card slide-in">
                  <!-- Email Field – fully glassy -->
                  <v-text-field v-model="login.email"
                                label="Email Address"
                                required
                                type="email"
                                class="glassy-input"
                                outlined
                                dense
                                prepend-inner-icon="mdi-email"
                                :rules="[v => !!v || 'Email is required', v => /.+@.+/.test(v) || 'Email must be valid']">
                  </v-text-field>

                  <!-- Password Field – fully glassy with view toggle -->
                  <v-text-field v-model="login.password"
                                label="Password"
                                required
                                :type="showPassword ? 'text' : 'password'"
                                class="glassy-input password-field"
                                outlined
                                dense
                                prepend-inner-icon="mdi-lock"
                                :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                @click:append="togglePasswordVisibility"
                                :rules="[v => !!v || 'Password is required']">
                  </v-text-field>

                  <!-- Form Actions -->
                  <div class="form-actions">
                    <v-btn :depressed="true"
                           type="submit"
                           color="primary"
                           :loading="$loadingTrue"
                           class="glassy-btn">
                      <span v-if="!$loadingTrue">Sign In</span>
                      <span v-else>Signing In...</span>
                    </v-btn>
                    <router-link :to="{ name: 'ResetPassword' }" class="forgot-link">
                      Forgot password?
                    </router-link>
                  </div>

                  <!-- Back to Home -->
                  <div class="back-home-wrapper">
                    <router-link :to="{ name: 'Index' }" class="back-link">
                      <v-icon small class="mr-1">mdi-arrow-left</v-icon> Back to Home
                    </router-link>
                  </div>
                </v-form>
              </div>
            </v-card>

            <!-- Partner Logos -->
            <div class="partner-logos-wrapper slide-in-delayed">
              <v-row class="partner-logos" justify="center" align="center">
                <v-col cols="auto" v-for="(logo, idx) in partnerLogos" :key="idx">
                  <img :src="logo.src" :alt="logo.alt" class="partner-logo" />
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-content>
  </v-app>
</template>

<script>
  export default {
    data() {
      return {
        login: {
          email: '',
          password: ''
        },
        showPassword: false,
        partnerLogos: [
          { src: require('@/assets/Both/Logos/BoranBeefLogoHorizontal.png'), alt: 'Boran Beef' },
          { src: require('@/assets/Both/Logos/MoraniCenterLogo_horizontal.png'), alt: 'Morani Center' },
          { src: require('@/assets/Both/Logos/SweewatersSanctuaryLogo_horizontal.png'), alt: 'Sweewaters Sanctuary' },
          { src: require('@/assets/Both/Logos/OPRanchingLogo.png'), alt: 'OP Ranching' },
          { src: require('@/assets/Both/Logos/Safari_Diner_logo.png'), alt: 'Safari Diner' }
        ]
      }
    },
    methods: {
      doAuth() {
        this.$store.dispatch('loading_true')
        this.$store.dispatch('do_login', this.login)
      },
      togglePasswordVisibility() {
        this.showPassword = !this.showPassword
      }
    }
  }
</script>

<style scoped>
  /* --- Global Layout --- */
  .glassy-app {
    min-height: 100vh;
    background: #f0f2f5;
  }

  .login-content {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: radial-gradient(ellipse at 50% 50%, rgba(116, 165, 51, 0.15) 0%, rgba(240, 242, 245, 0.85) 100%), url('https://www.olpejetaconservancy.org/wp-content/uploads/2024/07/Cape-Buffalo-1-scaled.webp') no-repeat center center fixed;
    background-size: cover;
    padding: 20px;
    position: relative;
    z-index: 1;
  }

  .login-container {
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
  }

  /* --- Slide-in Animations --- */
  .slide-in {
    animation: slideUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    opacity: 0;
    transform: translateY(40px);
  }

  .slide-in-delayed {
    animation: slideUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.15s forwards;
    opacity: 0;
    transform: translateY(30px);
  }

  @keyframes slideUp {
    0% {
      opacity: 0;
      transform: translateY(40px);
    }

    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* --- Glassy Card — fully translucent --- */
  .glassy-card {
    background: rgba(255, 255, 255, 0.15) !important;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.10);
    padding: 40px 36px 48px;
    transition: all 0.4s ease;
    position: relative;
    z-index: 10;
  }

    .glassy-card:hover {
      background: rgba(255, 255, 255, 0.25) !important;
      box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
    }

  .card-inner {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  /* --- Header --- */
  .login-header {
    text-align: center;
    margin-bottom: 28px;
  }

  .logo-image {
    max-width: 180px;
    height: auto;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.10));
    margin-bottom: 16px;
    transition: transform 0.3s ease;
  }

    .logo-image:hover {
      transform: scale(1.04);
    }

  .welcome-title {
    font-family: 'Inter', 'Segoe UI', sans-serif;
    font-size: 32px;
    font-weight: 700;
    color: #1e2a3a;
    text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5);
    margin: 8px 0 4px;
    letter-spacing: 1px;
  }

  .subtitle-text {
    font-family: 'Inter', 'Segoe UI', sans-serif;
    font-size: 16px;
    color: #3d4f5f;
    font-weight: 400;
    margin: 0;
    letter-spacing: 0.3px;
  }

  /* --- Form --- */
  .login-form {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  /* --- Glassy Inputs — fully translucent with blur --- */
  .glassy-input .v-input__slot {
    background: rgba(255, 255, 255, 0.25) !important;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 12px;
    transition: all 0.3s ease;
    }

    .glassy-input .v-input__slot:hover {
      background: rgba(255, 255, 255, 0.40) !important;
      border-color: rgba(255, 255, 255, 0.6);
    }

    .glassy-input .v-input__slot:focus-within {
      background: rgba(255, 255, 255, 0.55) !important;
      border-color: #74a533;
      box-shadow: 0 0 0 3px rgba(116, 165, 51, 0.15);
    }

  /* Label */
  .glassy-input .v-label {
    color: #2d3748 !important;
    font-weight: 500;
    font-size: 15px;
  }

  /* Input text */
  .glassy-input input {
    color: #1e2a3a !important;
    font-size: 16px;
    caret-color: #74a533;
  }

    .glassy-input input::placeholder {
      color: #6b7a8a !important;
    }

  /* Icon color */
  .glassy-input .v-input__prepend-inner .v-icon,
  .glassy-input .v-input__append .v-icon {
    color: #4a5568;
    transition: color 0.3s ease;
    cursor: pointer;
  }

  .glassy-input .v-input__append .v-icon {
    font-size: 24px;
    opacity: 0.9;
    color: #2d3748;
  }

  .glassy-input .v-input__append .v-icon:hover {
    color: #74a533;
    transform: scale(1.1);
  }

  .glassy-input:focus-within .v-input__prepend-inner .v-icon {
    color: #74a533;
  }

  /* Outline fieldset */
  .glassy-input.v-text-field--outlined .v-input__slot fieldset {
    border-color: rgba(255, 255, 255, 0.35);
    border-width: 1px;
  }

  .glassy-input.v-text-field--outlined .v-input__slot:hover fieldset {
    border-color: rgba(255, 255, 255, 0.6);
  }

  .glassy-input.v-text-field--outlined .v-input__slot:focus-within fieldset {
    border-color: #74a533;
  }

  /* Password field specific - ensure eye icon is visible */
  .password-field .v-input__append {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    padding: 4px;
    margin-right: 8px;
  }

  .password-field .v-input__append .v-icon {
    color: #1e2a3a !important;
    opacity: 1 !important;
  }

  .password-field .v-input__append .v-icon:hover {
    color: #74a533 !important;
  }

  /* --- Glassy Button --- */
  .glassy-btn {
    background: rgba(116, 165, 51, 0.70) !important;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.30);
    color: white !important;
    border-radius: 60px;
    padding: 12px 36px;
    font-family: 'Inter', 'Segoe UI', sans-serif;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    transition: all 0.25s ease;
    box-shadow: 0 6px 20px rgba(116, 165, 51, 0.20);
    min-width: 140px;
  }

    .glassy-btn:hover {
      background: rgba(116, 165, 51, 0.90) !important;
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 10px 30px rgba(116, 165, 51, 0.30);
    }

    .glassy-btn:active {
      transform: translateY(0) scale(0.98);
    }

    .glassy-btn .v-btn__content {
      color: white !important;
    }

  /* --- Glassy Links --- */
  .forgot-link {
    font-family: 'Inter', 'Segoe UI', sans-serif;
    color: #1e2a3a;
    text-decoration: none;
    font-weight: 500;
    font-size: 15px;
    transition: all 0.2s ease;
    padding: 6px 14px;
    border-radius: 30px;
    background: rgba(255, 255, 255, 0.30);
    backdrop-filter: blur(4px);
    border: 1px solid rgba(255, 255, 255, 0.20);
  }

    .forgot-link:hover {
      color: #74a533;
      background: rgba(255, 255, 255, 0.55);
    }

  .back-home-wrapper {
    text-align: center;
    margin-top: 22px;
  }

  .back-link {
    font-family: 'Inter', 'Segoe UI', sans-serif;
    color: #1e2a3a;
    text-decoration: none;
    font-weight: 500;
    font-size: 15px;
    display: inline-flex;
    align-items: center;
    padding: 6px 18px;
    border-radius: 40px;
    background: rgba(255, 255, 255, 0.30);
    backdrop-filter: blur(4px);
    border: 1px solid rgba(255, 255, 255, 0.20);
    transition: all 0.2s ease;
  }

    .back-link:hover {
      color: #74a533;
      background: rgba(255, 255, 255, 0.55);
    }

    .back-link .v-icon {
      font-size: 20px;
    }

  /* --- Partner Logos — also glassy --- */
  .partner-logos-wrapper {
    margin-top: 32px;
  }

  .partner-logos {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.20);
    padding: 12px 8px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 4px;
    margin: 0;
  }

  .partner-logo {
    height: 40px;
    width: auto;
    filter: brightness(0.90) saturate(0.80);
    transition: all 0.3s ease;
    margin: 4px 8px;
  }

    .partner-logo:hover {
      filter: brightness(1) saturate(1);
      transform: scale(1.08);
    }

  /* --- Responsive --- */
  @media (max-width: 960px) {
    .glassy-card {
      padding: 32px 24px 40px;
      border-radius: 16px;
    }

    .welcome-title {
      font-size: 28px;
    }

    .glassy-btn {
      padding: 10px 28px;
      font-size: 16px;
      min-width: 120px;
    }

    .partner-logo {
      height: 34px;
      margin: 2px 6px;
    }
  }

  @media (max-width: 600px) {
    .login-content {
      padding: 12px;
    }

    .glassy-card {
      padding: 24px 16px 32px;
      border-radius: 14px;
    }

    .welcome-title {
      font-size: 24px;
    }

    .subtitle-text {
      font-size: 14px;
    }

    .form-actions {
      flex-direction: column;
      align-items: stretch;
    }

    .glassy-btn {
      width: 100%;
      justify-content: center;
      font-size: 16px;
      padding: 12px;
    }

    .forgot-link {
      text-align: center;
    }

    .partner-logo {
      height: 28px;
      margin: 2px 4px;
    }

    .partner-logos {
      padding: 8px 4px;
    }
  }

  /* --- Global Vuetify overrides (scoped deep selectors) --- */
  .glassy-app ::v-deep .v-application--wrap {
    background: transparent !important;
  }

  .glassy-app ::v-deep .glassy-input.v-text-field--outlined.v-input--is-focused .v-label {
    color: #74a533 !important;
  }

  .glassy-app ::v-deep .glassy-btn .v-progress-circular {
    color: white !important;
  }

  .glassy-app ::v-deep .v-input--is-focused .v-input__slot {
    background: rgba(255, 255, 255, 0.55) !important;
  }
</style>